import React, { useEffect, useState, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import PaginationTable from '../../pagination/Pagination4';

/* ================= API ================= */
const KeyRegisterAllotFPS_data = async (parms) => {
  const response = await fetch(
    `${process.env.REACT_APP_URL_ALLOTMENT}/KeyRegister/get_fps_keyreg_allot`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(parms),
    }
  );

  if (!response.ok) throw new Error('Failed to fetch data');
  return response.json();
};

/* ================= COMPONENT ================= */
const KeyRegisterAllotFPS = ({ month, year, comm_id, dist_code, afso_code }) => {

  /* ---------- STATE ---------- */
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabHeading, setTabHeading] = useState(null);

  const [nfsaSchemeList, setNfsaSchemeList] = useState([]);
  const [stateSchemeList, setStateSchemeList] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const fpstab = useRef(null);

  /* ---------- FETCH DATA ---------- */
  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const res = await KeyRegisterAllotFPS_data({
          month, year, comm_id, dist_code, afso_code
        });

        setTabHeading(res.heading || '');
        setReportData(res.data || []);

        const nfsa = [];
        const state = [];

        (res.data || []).forEach(item => {
          (item.schemeWrapperList || []).forEach(w => {
            if (w.nfsaSchemeList) nfsa.push(...w.nfsaSchemeList);
            if (w.stateSchemeList) state.push(...w.stateSchemeList);
          });
        });

        setNfsaSchemeList(
          Array.from(new Map(nfsa.map(s => [s.scheme_id, s])).values())
        );
        setStateSchemeList(
          Array.from(new Map(state.map(s => [s.scheme_id, s])).values())
        );

      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, [month, year, comm_id, dist_code, afso_code]);

  /* ---------- RESET PAGE ON SEARCH ---------- */
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  /* ---------- FILTER ---------- */
  const filteredData = React.useMemo(() => {
    if (!searchQuery.trim()) return reportData;

    const query = searchQuery.toLowerCase().trim();

    return reportData.filter(item => {

      if (String(item.fps_id || '').toLowerCase().includes(query)) return true;
      if (String(item.total_Cards || '').includes(query)) return true;
      if (String(item.total_units || '').includes(query)) return true;
      if (String(item.totalAllottedQty || '').includes(query)) return true;


      return (item.schemeWrapperList || []).some(wrapper => {
        const allSchemesInWrapper = [
          ...(wrapper.nfsaSchemeList || []),
          ...(wrapper.stateSchemeList || [])
        ];

        return allSchemesInWrapper.some(scheme =>
          String(scheme.alloted_qty || '').includes(query) ||
          (scheme.scheme_short_name || '').toLowerCase().includes(query)
        );
      });
    });
  }, [reportData, searchQuery]);

  /* ---------- PAGINATION ---------- */
  const paginatedData =
    rowsPerPage >= filteredData.length
      ? filteredData
      : filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value === 'all' ? filteredData.length : parseInt(value, 10));
    setPage(0);
  };

  /* ---------- AUTO SCROLL ---------- */
  useEffect(() => {
    if (!loading && !error && reportData.length > 0 && fpstab.current) {
      fpstab.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, error, reportData]);

  /* ---------- UI STATES ---------- */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <div className="mainhead" align="center">Internal Error</div>;
  }

  if (filteredData.length === 0) {
    return <div className="mainhead" align="center">No Data found</div>;
  }

  /* ---------- TOTALS ---------- */
  const allSchemes = [...nfsaSchemeList, ...stateSchemeList];

  const totals = {
    total_Cards: 0,
    total_units: 0,
    totalAllottedQty: 0,
    schemes: {}
  };

  allSchemes.forEach(s => totals.schemes[s.scheme_id] = 0);

  filteredData.forEach(item => {
    totals.total_Cards += item.total_Cards || 0;
    totals.total_units += item.total_units || 0;

    let rowTotal = 0;

    allSchemes.forEach(scheme => {
      let qty = 0;

      (item.schemeWrapperList || []).forEach(w => {
        const nf = w.nfsaSchemeList?.find(n => n.scheme_id === scheme.scheme_id);
        const st = w.stateSchemeList?.find(n => n.scheme_id === scheme.scheme_id);
        if (nf) qty = nf.alloted_qty || 0;
        if (st) qty = st.alloted_qty || 0;
      });

      totals.schemes[scheme.scheme_id] += qty;
      rowTotal += qty;
    });

    totals.totalAllottedQty += rowTotal;
  });

  /* ================= RENDER ================= */
  return (
    <>
      {/* Pagination + Search */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '90%',
          margin: '0 auto',
          marginBottom: '20px'
        }}
      >
        <PaginationTable
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        <input
          type="text"

          placeholder="Search FPS ID"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ padding: '3px', width: '300px' }}
        />
      </div>

      {/* TABLE */}
      <table ref={fpstab} align="center" width="90%" border={1}>
        <thead>
          <tr className="tableheader">
            <th colSpan={allSchemes.length * 2 + 5}>{tabHeading}</th>
          </tr>

          <tr className="tableheader">
            <th rowSpan={2}>Sl.No</th>
            <th rowSpan={2}>FPS</th>
            <th rowSpan={2}>Total Cards</th>
            <th rowSpan={2}>Total Units</th>

            {nfsaSchemeList.length > 0 &&
              <th colSpan={nfsaSchemeList.length * 2}>NFSA</th>}

            {stateSchemeList.length > 0 &&
              <th colSpan={stateSchemeList.length * 2}>Non-NFSA</th>}

            <th rowSpan={2}>Total Allotment</th>
          </tr>

          <tr className="tableheader">
            {nfsaSchemeList.map(s => (
              <th colSpan={2} key={s.scheme_id}>{s.scheme_short_name}</th>
            ))}
            {stateSchemeList.map(s => (
              <th colSpan={2} key={s.scheme_id}>{s.scheme_short_name}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {paginatedData.map((item, index) => (
            <tr key={item.fps_id}>
              <td>{index + 1 + page * rowsPerPage}</td>
              <td>{item.fps_id}</td>
              <td align="right">{item.total_Cards}</td>
              <td align="right">{item.total_units}</td>

              {allSchemes.map(scheme => {
                let qty = 0;
                (item.schemeWrapperList || []).forEach(w => {
                  const nf = w.nfsaSchemeList?.find(n => n.scheme_id === scheme.scheme_id);
                  const st = w.stateSchemeList?.find(n => n.scheme_id === scheme.scheme_id);
                  if (nf) qty = nf.alloted_qty || 0;
                  if (st) qty = st.alloted_qty || 0;
                });
                return (
                  <td key={scheme.scheme_id} colSpan={2} align="right">{qty}</td>
                );
              })}

              <td align="right">{item.totalAllottedQty}</td>
            </tr>
          ))}

          {/* TOTAL ROW */}
          <tr className="tableheader">
            <td colSpan={2} align="right">Total</td>
            <td align="right">{totals.total_Cards}</td>
            <td align="right">{totals.total_units}</td>

            {allSchemes.map(s => (
              <td key={s.scheme_id} colSpan={2} align="right">
                {totals.schemes[s.scheme_id]}
              </td>
            ))}

            <td align="right">{totals.totalAllottedQty}</td>
          </tr>
        </tbody>
      </table>

      <br /><br /> <br />
    </>
  );
};

export default KeyRegisterAllotFPS;

